import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import sounds from "@/utils/sounds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instagram SVG icon component
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail("");
      toast.success(t("footer.subscribeSuccess"));
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error(t("footer.subscribeError"));
    } finally {
      setSubscribing(false);
    }
  };

  const navLinks = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.services"), path: "/services" },
    { name: t("nav.contact"), path: "/contact" },
    { name: t("nav.reviews"), path: "/reviews" },
  ];

  return (
    <footer data-testid="footer" className="bg-navy-900 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <motion.div 
            className="flex flex-col md:flex-row items-center justify-between gap-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center md:text-left">
              <motion.h3 
                className="text-xl md:text-2xl font-bold font-heading mb-2"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, type: "spring" }}
              >
                {t("footer.stayUpdated")}
              </motion.h3>
              <motion.p 
                className="text-slate-400 text-sm md:text-base"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                {t("footer.newsletterDescription")}
              </motion.p>
            </div>
            
            {subscribed ? (
              <div className="flex items-center gap-3 bg-green-500/20 text-green-400 px-6 py-4 rounded-lg">
                <Check className="w-5 h-5" />
                <span className="font-medium">{t("footer.subscribeSuccess")}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex w-full md:w-80 lg:w-96 min-w-0 shrink-0 gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.emailPlaceholder")}
                  required
                  data-testid="newsletter-email"
                  className="flex-1 min-w-0 w-full bg-slate-800 border-0 text-white placeholder-slate-500 px-4 py-3 focus:ring-2 focus:ring-marine-500"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="newsletter-subscribe-btn"
                  aria-label={t("footer.subscribe")}
                  className="bg-marine-600 hover:bg-marine-500 text-white px-4 py-3 font-semibold transition-colors flex shrink-0 items-center gap-2 disabled:opacity-50"
                >
                  {subscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">{t("footer.subscribe")}</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src="/logo.png" alt="Shylov Cleaning Logo" className="h-12 w-auto" />
              <div>
                <span className="font-accent text-xl font-semibold tracking-wide block">
                  Shylov Cleaning
                </span>
                <span className="text-xs text-slate-400">Yachting d'Exception</span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t("footer.description")}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/shylov_cleaning/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 bg-slate-800 hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-500 hover:to-orange-400 flex items-center justify-center transition-all duration-300 group rounded-lg"
                aria-label="Follow us on Instagram"
              >
                <InstagramIcon className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-wider font-semibold mb-6">
              {t("footer.navigation")}
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-nav-${link.path.replace("/", "") || "home"}`}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/login" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t("nav.login")}
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t("nav.register")}
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t("footer.dashboard")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm uppercase tracking-wider font-semibold mb-6">
              {t("footer.ourServices")}
            </h4>
            <ul className="space-y-3">
              {["yachts", "automotive", "residential", "marble", "construction"].map(category => (
                <li key={category}><Link to={`/services?category=${category}`} data-testid={`footer-service-${category}`} className="text-slate-400 hover:text-white transition-colors text-sm">{t(`services.categories.${category}`)}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm uppercase tracking-wider font-semibold mb-6">
              {t("footer.contactUs")}
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="mailto:shylov.cleaning@gmail.com" data-testid="footer-email" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors text-sm">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>shylov.cleaning@gmail.com</span>
                </a>
              </li>
              <li>
                <a href="tel:+33756888821" data-testid="footer-phone-1" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>+33 (0)7 56 88 88 21</span>
                </a>
              </li>
              <li>
                <a href="tel:+33537078888" data-testid="footer-phone-2" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>+33 (0)5 37 07 88 88</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t("contact.locationValue")}</span>
                </div>
              </li>
              <li>
                <div className="flex items-center gap-3 text-slate-400 text-sm">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>{t("contact.hoursValue")}</span>
                </div>
              </li>
              <li>
                <a href="https://www.instagram.com/shylov_cleaning/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm">
                  <InstagramIcon className="w-4 h-4 flex-shrink-0" />
                  <span>@shylov_cleaning</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              &copy; {currentYear} Shylov Cleaning. {t("footer.rights")}
            </p>
            <div className="flex items-center gap-4">
              <Link 
                to="/terms" 
                className="text-slate-500 hover:text-white text-sm transition-colors"
                data-testid="footer-terms"
              >
                {t("footer.terms")}
              </Link>
              <span className="text-slate-700">|</span>
              <p className="text-slate-500 text-sm">
                {t("footer.tagline")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
